<template>
    <p-dialog header=" " :visible.sync="visible" :showHeader="false" :closable="false" @hide="onModalCancel()"
        :modal="true" class="tradingview-fullpage-modal">
        <div class="auth-modal-wrapper dark-theme">
            <!-- Left Hero Banner Card (TradingView Style) -->
            <div class="auth-hero-banner">
                <img src="/images/Create-account.webp" alt="ProTrader - Scan 1000's of Stocks" class="hero-full-banner-img" />
            </div>

            <!-- Right Authentication Content Panel -->
            <div class="auth-content-panel">
                <!-- Top Header Navigation Bar -->
                <div class="auth-panel-header">
                    <div class="header-left">
                        <button v-if="authStep === 'email' && !showResendEmail" class="back-btn" @click="authStep = 'initial'" title="Back">
                            <i class="pi pi-arrow-left"></i>
                        </button>
                    </div>
                    <div class="header-center">
                        <Header></Header>
                    </div>
                    <div class="header-right">
                        <button class="close-btn" @click="onModalCancel" title="Close">
                            <i class="pi pi-times"></i>
                        </button>
                    </div>
                </div>

                <!-- Scrollable Body Content -->
                <div class="auth-panel-body">
                    <!-- Resend Email View -->
                    <div v-if="showResendEmail" class="auth-view-container">
                        <ResendEmail @resendEmail="handleResendEmail" :isResendLoading="isResendLoading"
                            :isResponseMessage="isResponseMessage"></ResendEmail>
                    </div>

                    <!-- Main Auth Steps -->
                    <div v-else class="auth-view-container">
                        <!-- Step 1: Initial Screen (Screenshot 1) -->
                        <div v-if="authStep === 'initial'" class="initial-auth-step">
                            <h2 class="auth-title">Sign up</h2>

                            <!-- Google SSO Button -->
                            <div class="sso-container">
                                <GoogleSsoBtn label="Continue with Google" @click="loginWithGoogle" />
                            </div>

                            <!-- Divider -->
                            <div class="sso-divider">
                                <span>or</span>
                            </div>

                            <!-- Email Option Button -->
                            <div class="email-option-wrapper">
                                <button type="button" class="email-option-btn" @click="authStep = 'email'">
                                    <i class="pi pi-envelope email-icon"></i>
                                    <span>Email</span>
                                </button>
                            </div>

                            <!-- Footer Link -->
                            <div class="auth-footer-link">
                                <span>Already have an account? </span>
                                <router-link style="text-decoration: none !important;" :to="{ name: 'login' }" @click.native="onModalCancel">
                                    <span class="sign-in-text">Sign in</span>
                                </router-link>
                            </div>
                        </div>

                        <!-- Step 2: Email Screen (Screenshot 2) -->
                        <div v-else-if="authStep === 'email'" class="email-auth-step">
                            <h2 class="auth-title">Sign up with email</h2>

                            <div v-if="showResponseError" class="errorText error-banner">
                                {{ showResponseError }}
                            </div>

                            <form @submit.prevent="handleCreateAccount" class="email-form">
                                <!-- Email Field -->
                                <div class="form-group-field">
                                    <label class="input-label">Email</label>
                                    <DynamicInput type="text" placeholder="Email" :isError="isEmailError"
                                        :iconImg="require(`@/assets/${isEmailError ? 'redEmail.svg' : 'email.svg'}`)"
                                        v-model="email" />
                                    <div class="field-error-text">
                                        <span v-if="emailErrorMsg === 'already exits'" class="errorText">⚠ It seems you already have an account with Pro Trader.
                                            <router-link :to="{ name: 'login' }" style="color:#F20E17;" @click.native="onModalCancel">
                                                <span style="text-decoration: underline;cursor: pointer;">Login here</span>
                                            </router-link>
                                            or
                                            <router-link :to="{ name: 'ResetPassword' }" style="color:#F20E17;" @click.native="onModalCancel">
                                                <span style="text-decoration: underline;cursor: pointer;">reset your password</span>
                                            </router-link>
                                        </span>
                                        <span v-else-if="emailErrorMsg" class="errorText">{{ emailErrorMsg }}</span>
                                    </div>
                                </div>

                                <!-- Password Field -->
                                <div class="form-group-field">
                                    <label class="input-label">Password</label>
                                    <DynamicInput :type="showPassword ? 'text' : 'password'" placeholder="Password"
                                        :iconImg="require(`@/assets/${isPasswordError ? 'redEye.svg' : showPassword ? 'openEye.svg' : 'closeEye.svg'}`)"
                                        v-model="password" :isError="isPasswordError"
                                        @password-visibility-toggled="handlePasswordVisibilityToggle" />

                                    <!-- Password Strength Indicator -->
                                    <div class="strongPassword">
                                        <div class="strength-bars">
                                            <div class="strongPasswordLine" :style="{
                                                height: '4px',
                                                background: weakPassword === 0 ? '#F20E17' : '#2a2e39',
                                                width: '33%',
                                            }"></div>
                                            <div class="strongPasswordLine" :style="{
                                                height: '4px',
                                                background: mediumPassword === 1 ? '#e2e522' : '#2a2e39',
                                                width: '33%',
                                            }"></div>
                                            <div class="strongPasswordLine" :style="{
                                                height: '4px',
                                                background: strongPassword === 2 ? '#4ece31' : '#2a2e39',
                                                width: '33%',
                                            }"></div>
                                        </div>
                                        <div v-if="passwordErrorMsg" class="field-error-text">
                                            <span class="errorText">{{ showWarningIcon ? '⚠ ' : '' }}{{ passwordErrorMsg }}</span>
                                        </div>
                                        <div v-if="strongPassword">
                                            <span class="successText">Your Password is strong. Good Job!</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Submit Button -->
                                <div class="submit-btn-wrapper">
                                    <button type="submit" class="submit-create-btn enabled"
                                        :disabled="isLoading">
                                        <span v-if="isLoading" class="loader-spinner">
                                            <Loader></Loader>
                                        </span>
                                        <span>{{ isLoading ? 'Creating an Account...' : 'Create account' }}</span>
                                        <i v-if="!isLoading" class="pi pi-arrow-right arrow-icon"></i>
                                    </button>
                                </div>

                                <!-- Terms & Privacy Notice -->
                                <div class="legal-notice">
                                    By creating an account, you agree to our <span>T&Cs</span> and have read and acknowledge the <span>Global Privacy Statement</span>.
                                </div>
                            </form>

                            <!-- Footer Link -->
                            <div class="auth-footer-link">
                                <span>Already have an account? </span>
                                <router-link  style="text-decoration: none !important;" :to="{ name: 'login' }" @click.native="onModalCancel">
                                    <span class="sign-in-text">Sign in</span>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </p-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import config from '../../../config';
import { createAccount, resendEmail } from '../../../services/user';
import { getStoredGclid } from '../../../utils/gclid';
import DynamicInput from '../../DynamicInput.vue';
import Header from '../../Header.vue';
import Loader from '../../Loader.vue';
import ResendEmail from '../../ResendEmail.vue';
import GoogleSsoBtn from '../../googleSsoBtn.vue';

export default {
    name: 'SignUpModal',
    components: {
        Header,
        DynamicInput,
        Loader,
        ResendEmail,
        GoogleSsoBtn
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            authStep: 'initial', // 'initial' | 'email'
            monthly_priceId: config.Monthly_priceId,
            yearly_priceId: config.Yearly_priceId,

            email: '',
            password: '',
            referralCode: '',
            priceId: null,
            showResponseError: "",
            isEmailError: false,
            emailErrorMsg: "",
            showPassword: false,
            isPasswordError: false,
            weakPassword: null,
            mediumPassword: null,
            strongPassword: null,
            passwordErrorMsg: "",
            showWarningIcon: false,
            isLoading: false,
            emailRegix: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
            passwordRegix: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{10,}$/,
            showResendEmail: false,

            isResponseMessage: "",
            isResendLoading: false,
            
            // Product selection
            selectedProductCurrency: null,
        }
    },
    mounted() {
        if (this.$route.query && this.$route.query.ref) {
            this.referralCode = this.$route.query.ref
        }
        if (this.selectedPriceId === config.Yearly_priceId) {
            this.priceId = config.Yearly_priceId
        }
        else {
            this.priceId = config.Monthly_priceId
        }
    },
    methods: {
        ...mapActions(['changeAuthModal']),
        onModalCancel() {
            this.changeAuthModal(false)
            this.showResendEmail = false
            this.authStep = 'initial'
        },
        handlePasswordVisibilityToggle() {
            this.showPassword = !this.showPassword
        },

        loginWithGoogle() {
            const base = config.USER_API
            let url = `${base}auth/google`

            let { currency, product, ref } = this.$route.query;
            let referral = ref || this.referralCode || '';
            if (currency && product) {
                sessionStorage.setItem('pending_registration', JSON.stringify({ currency, product, ref: referral }));
            }

            const params = new URLSearchParams();
            const gclid = getStoredGclid();
            const stateObj = {
                referral: referral,
                ...(gclid ? { gclid } : {})
            };
            params.append('state', JSON.stringify(stateObj));
            if (gclid) params.append('gclid', gclid);

            if (currency) params.append('currency', currency);
            if (product) params.append('product', product);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }
            window.location.href = url;
        },

        async handleCreateAccount() {

            if (!this.email && !this.password) {
                this.isEmailError = true
                this.emailErrorMsg = "*Required Field"
                this.isPasswordError = true
                this.passwordErrorMsg = "*Required Field"
                return
            }

            if (!this.email) {
                this.isEmailError = true
                this.emailErrorMsg = "*Required Field"
                return
            }
            if (!this.password) {
                this.isPasswordError = true
                this.passwordErrorMsg = "*Required Field"
                return
            }

            if (this.email) {
                if (!this.emailRegix.test(this.email)) {
                    this.isEmailError = true
                    this.emailErrorMsg = "Invalid Email"
                    return
                }
            }
            if (this.password) {
                if (!this.passwordRegix.test(this.password)) {
                    this.isPasswordError = true
                    this.strongPassword = null
                    this.showWarningIcon = true
                    this.passwordErrorMsg = "The password must be more than 9 characters including uppercase, lowercase, numbers and special characters."
                    return
                }
            }

            const { currency, product } = this.$route.query;

            if (currency && product) {
                let periodPrefix = '';
                if (product.startsWith('M')) periodPrefix = 'Monthly';
                else if (product.startsWith('Y')) periodPrefix = 'Yearly';

                if (periodPrefix) {
                    let variant = '';
                    if (product.includes('USAONLY')) variant = '_noASX';
                    else if (product.includes('FORUMONLY')) variant = '_ForumOnly';

                    let currencySuffix = '';
                    if (currency === 'AUD') currencySuffix = '_AUD';

                    const configKey = `${periodPrefix}${variant}${currencySuffix}_priceId`;
                    console.log("cinfigKey: ", configKey);
                    const priceId = config[configKey];
                    console.log("priceId: ", priceId);

                    if (priceId) {
                        this.proceedWithAccountCreation({
                            priceId: priceId,
                            currency: currency
                        });
                        return;
                    }
                }
            }

            this.proceedWithAccountCreation(false);
        },

        async proceedWithAccountCreation(productData) {
            if (productData) {
                this.$store.commit('SET_PRICE_ID', productData.priceId)
                this.selectedProductCurrency = productData.currency
            }

            this.isLoading = true
            const gclid = getStoredGclid();
            const payload = {
                email: this.email,
                password: this.password,
                referralCode: this.referralCode || null,
                ...(gclid ? { gclid } : {})
            }

            if (productData.priceId) {
                payload["stripeId"] = productData.priceId;
            }

            const res = await createAccount(payload)
            if (res?.data?.success) {
                this.showResendEmail = true
            } else {
                if (res?.data?.message == "User Alredy Exists!") {
                    this.isEmailError = true
                    this.emailErrorMsg = "already exits"
                } else {
                    this.showResponseError = res?.data?.message
                    setTimeout(() => {
                        this.showResponseError = ""
                    }, 5000);
                }
            }
            this.isLoading = false
        },

        calculatePasswordStrength(pass) {
            if (pass?.length > 0) {
                if (pass?.length <= 3) {
                    this.weakPassword = 0
                    this.mediumPassword = null
                    this.strongPassword = null
                } else if (pass?.length <= 8) {
                    this.weakPassword = 0
                    this.mediumPassword = 1
                    this.strongPassword = null
                } else if (pass?.length > 9) {
                    if (this.passwordRegix.test(pass)) {
                        this.weakPassword = 0
                        this.mediumPassword = 1
                        this.strongPassword = 2
                    }
                }
            } else {
                this.weakPassword = null
                this.mediumPassword = null
                this.strongPassword = null
            }
        },
        async handleResendEmail() {
            this.isResendLoading = true
            const payload = {
                email: this.email,
                password: this.password,
            }

            const { currency, product } = this.$route.query;

            if (currency && product) {
                let periodPrefix = '';
                if (product.startsWith('M')) periodPrefix = 'Monthly';
                else if (product.startsWith('Y')) periodPrefix = 'Yearly';

                if (periodPrefix) {
                    let variant = '';
                    if (product.includes('USAONLY')) variant = '_noASX';
                    else if (product.includes('FORUMONLY')) variant = '_ForumOnly';

                    let currencySuffix = '';
                    if (currency === 'AUD') currencySuffix = '_AUD';

                    const configKey = `${periodPrefix}${variant}${currencySuffix}_priceId`;
                    console.log("cinfigKey: ", configKey);
                    const priceId = config[configKey];
                    console.log("priceId: ", priceId);

                    if (priceId) {
                        payload["stripeId"] = priceId;
                    }
                }
            }

            const res = await resendEmail(payload)
            if (res?.data?.success) {
                this.isResponseMessage = res?.data?.message
            } else {
                this.isResponseMessage = res?.data?.message
            }
            setTimeout(() => {
                this.isResponseMessage = ""
            }, 5000);
            this.isResendLoading = false
        }
    },
    watch: {
        visible(newVal) {
            if (!newVal) {
                this.authStep = 'initial';
            }
        },
        password(newPassword) {
            this.calculatePasswordStrength(newPassword);
            this.isPasswordError = false
            this.passwordErrorMsg = ""
            this.showWarningIcon = false
        },
        email() {
            this.isEmailError = false
            this.emailErrorMsg = ""
        },
        selectedPriceId(newVal) {
            if (newVal === config.Yearly_priceId) {
                this.priceId = config.Yearly_priceId
            }
            else {
                this.priceId = config.Monthly_priceId
            }
        }
    },
    computed: {
        ...mapGetters([
            'selectedPriceId',
            'themeDarkMode'
        ])
    }
}
</script>

<style scoped>
/* PrimeVue Dialog Overrides for Full-Page Layout */
:deep(.p-dialog.tradingview-fullpage-modal) {
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    margin: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    border: none !important;
}

:deep(.p-dialog.tradingview-fullpage-modal .p-dialog-content) {
    padding: 0 !important;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
    background: transparent !important;
}

/* Modal Main Grid Wrapper */
.auth-modal-wrapper {
    display: flex;
    width: 100vw;
    height: 100vh;
    background-color: #0c0a0e;
    color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Trebuchet MS, Ubuntu, sans-serif;
    box-sizing: border-box;
    padding: 16px;
    gap: 16px;
}

.auth-modal-wrapper.dark-theme {
    background-color: #0c0a0e;
    color: #ffffff;
}

/* Left Hero Banner Card (TradingView Style) */
.auth-hero-banner {
    flex: 1;
    height: 100%;
    border-radius: 24px;
    background-color: #000000;
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    box-sizing: border-box;
    border: 1px solid rgba(242, 14, 23, 0.25);
    box-shadow: 0 0 30px rgba(242, 14, 23, 0.08);
}

.dark-theme .auth-hero-banner {
    background-color: #000000;
}

.hero-full-banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 24px;
    display: block;
}

.hero-footer-branding {
    z-index: 2;
}

.hero-brand-logo {
    height: 36px;
    filter: brightness(0) invert(1);
    opacity: 0.9;
}

/* Right Content Panel */
.auth-content-panel {
    width: 45%;
    max-width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    background: radial-gradient(circle at 100% 0%, rgba(242, 14, 23, 0.12) 0%, transparent 45%), #0c0a0e;
    position: relative;
    flex-shrink: 0;
    border-radius: 24px;
    border: 1px solid rgba(242, 14, 23, 0.15);
}

/* Header Navigation Bar */
.auth-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    position: relative;
}

.header-left, .header-right {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.header-center {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 30px;
}

:deep(.header-center img) {
    filter: brightness(0) invert(1) drop-shadow(0 0 12px rgba(242, 14, 23, 0.35));
    opacity: 0.95;
    max-height: 44px;
}

.back-btn, .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b0b4c3;
    transition: background-color 0.2s, transform 0.15s ease, color 0.2s;
}

.close-btn i, .back-btn i {
    font-size: 2.25rem;
    font-weight: 700;
}

.dark-theme .back-btn, .dark-theme .close-btn {
    color: #b0b4c3;
}

.back-btn:hover, .close-btn:hover {
    background-color: rgba(242, 14, 23, 0.15);
    color: #ffffff;
    transform: scale(1.1);
}

/* Scrollable Body */
.auth-panel-body {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 32px 48px;
}

.auth-view-container {
    width: 100%;
    max-width: 360px;
    margin: 0 auto;
}

.auth-title {
    font-size: 1.75rem;
    font-weight: 700;
    text-align: center;
    margin-bottom: 32px;
    color: #ffffff;
    letter-spacing: -0.5px;
}

/* SSO & Divider */
.sso-container {
    display: flex;
    justify-content: center;
    width: 100%;
}

:deep(.google-sso-btn) {
    width: 100% !important;
    max-width: 100% !important;
    height: 48px !important;
    background-color: #17141b !important;
    border: 1px solid #2e2329 !important;
    border-radius: 8px !important;
    color: #ffffff !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    box-shadow: none !important;
    transition: all 0.2s ease !important;
}

:deep(.google-sso-btn:hover) {
    background-color: #221c25 !important;
    border-color: #F20E17 !important;
    box-shadow: 0 4px 18px rgba(242, 14, 23, 0.25) !important;
    transform: translateY(-1px) !important;
}

.sso-divider {
    display: flex;
    align-items: center;
    text-align: center;
    color: #726b77;
    font-size: 14px;
    margin: 24px 0;
}

.sso-divider::before,
.sso-divider::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #261e25;
}

.dark-theme .sso-divider::before,
.dark-theme .sso-divider::after {
    border-bottom-color: #261e25;
}

.sso-divider span {
    padding: 0 12px;
}

/* Email Option Button (Initial Screen) */
.email-option-wrapper {
    margin: 20px 0;
}

.email-option-btn {
    width: 100%;
    height: 48px;
    background: #17141b;
    border: 1px solid #2e2329;
    border-radius: 8px;
    color: #ffffff;
    font-size: 1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.dark-theme .email-option-btn {
    background: #17141b;
    border-color: #2e2329;
    color: #ffffff;
}

.email-option-btn:hover {
    border-color: #F20E17;
    color: #ffffff;
    background: #221c25;
    box-shadow: 0 4px 18px rgba(242, 14, 23, 0.25);
    transform: translateY(-1px);
}

.email-icon {
    font-size: 1.1rem;
    color: #F20E17;
}

/* Form Controls (Email Screen) */
.email-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.form-group-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.input-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: #9295a0;
}

:deep(.input-container input) {
    background-color: #17141b !important;
    border: 1px solid #2e2329 !important;
    color: #ffffff !important;
    border-radius: 8px !important;
    height: 48px !important;
    transition: all 0.2s ease !important;
}

:deep(.input-container input::placeholder) {
    color: #5c5562 !important;
}

:deep(.input-container input:focus) {
    border-color: #F20E17 !important;
    box-shadow: 0 0 0 2px rgba(242, 14, 23, 0.3) !important;
}

:deep(.input-icon) {
    background: transparent !important;
}

.field-error-text {
    min-height: 18px;
    margin-top: 2px;
}

.errorText {
    color: #F20E17;
    font-size: 0.8rem;
}

.successText {
    color: #4ece31;
    font-size: 0.8rem;
}

.error-banner {
    margin-bottom: 12px;
    text-align: center;
    background: rgba(242, 14, 23, 0.1);
    border: 1px solid rgba(242, 14, 23, 0.3);
    border-radius: 6px;
    padding: 8px;
}

/* Strength Meter */
.strongPassword {
    margin-top: 6px;
}

.strength-bars {
    display: flex;
    gap: 4px;
    margin-bottom: 4px;
}

/* Submit CTA */
.submit-btn-wrapper {
    margin-top: 8px;
}

.submit-create-btn {
    width: 100%;
    height: 48px;
    border: none;
    border-radius: 8px;
    background-color: #221c25;
    color: #5c5562;
    font-size: 1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: not-allowed;
    transition: all 0.25s ease;
}

.submit-create-btn.enabled {
    background: linear-gradient(135deg, #F20E17 0%, #c40008 100%);
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 4px 18px rgba(242, 14, 23, 0.45);
}

.submit-create-btn.enabled:hover {
    background: linear-gradient(135deg, #ff242d 0%, #b30007 100%);
    box-shadow: 0 6px 24px rgba(242, 14, 23, 0.6);
    transform: translateY(-1px);
}

.loader-spinner {
    margin-right: 8px;
}

/* Legal Notice */
.legal-notice {
    font-size: 0.75rem;
    color: #6d6673;
    text-align: center;
    line-height: 1.4;
    margin-top: 8px;
}

.legal-notice span {
    color: #a49da8;
    text-decoration: underline;
    cursor: pointer;
}

.legal-notice span:hover {
    color: #F20E17;
}

/* Footer Links */
.auth-footer-link {
    margin-top: 32px;
    text-align: center;
    font-size: 0.9rem;
    color: #827b88;
}

.auth-footer-link a,
.auth-footer-link a:hover,
.auth-footer-link a:visited,
.auth-footer-link a:active,
.auth-footer-link a:focus {
    text-decoration: none !important;
    outline: none !important;
    border-bottom: none !important;
}

.sign-in-text {
    color: #F20E17;
    font-weight: 600;
    cursor: pointer;
    margin-left: 4px;
    text-decoration: none !important;
    border-bottom: none !important;
    display: inline-block;
    transition: color 0.15s ease;
}

.sign-in-text:hover,
.sign-in-text:focus,
.sign-in-text:active {
    color: #ff3840;
    text-decoration: none !important;
    border-bottom: none !important;
}

/* Resend Email Dark View Support */
:deep(.resend-container) {
    color: #d1d4dc !important;
}
:deep(.resend-container h1),
:deep(.resend-container h4),
:deep(.resend-container .mediumText) {
    color: #ffffff !important;
}
:deep(.resend-container .errorText) {
    color: #9295a0 !important;
}
:deep(.resend-container .errorText[style*="#F20E17"]) {
    color: #F20E17 !important;
}

/* Responsive adjustments */
@media screen and (max-width: 860px) {
    .auth-hero-banner {
        display: none;
    }
    .auth-content-panel {
        width: 100%;
    }
    .auth-modal-wrapper {
        padding: 0;
    }
}
</style>